import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { createStyles, makeStyles, Grid } from '@material-ui/core';
import { ExtendedTheme, Loader, Heading, Avatar } from 'neon-design-system';
import { Contributor, Channel } from '../../../shared/types';
import { CONTRIBUTOR_PAGE } from '../../../gql/contributors';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import SectionHeader from '../../ui/SectionHeader';
import { getInitials } from '../../../utils/string.utils';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useSecureFileUrl } from '../../hooks/use-secure-file-url';
import ChannelPreviewList from '../../ui/ChannelPreviewList';

interface ContributorDetailsProps {
	classes: any;
	channels?: Channel[];
	contributor: Contributor;
	imgLink?: string;
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	contributorAvatar: {
		backgroundColor: theme.themeColors.info[500],
		marginRight: theme.spacing(4),
		fontWeight: 'normal',
		[theme.breakpoints.down('xs')]: {
			marginRight: 0,
			marginBottom: theme.spacing(4)
		}
	},
	contributorDetailsWrapper: {
		display: 'flex',
		width: '100%',
		flexDirection: 'column',
		textAlign: 'center',
		[theme.breakpoints.up('sm')]: {
			textAlign: 'left'
		}
	},
	contributorDetailsTitle: {
		display: 'flex',
		height: '100%',
		alignItems: 'center',
		flexDirection: 'column',
		justifyContent: 'center',
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
			justifyContent: 'flex-start'
		}
	},
	contributorDetailsHeading: {
		textAlign: 'center',
		[theme.breakpoints.up('sm')]: {
			textAlign: 'left'
		}
	},
	contributions: {
		marginBottom: theme.spacing(4)
	},
	visible: {
		display: 'block'
	},
	hidden: {
		display: 'none'
	},
	marginTop: {
		marginTop: theme.spacing(2)
	}
}))

const ContributorDetails = ({ classes, contributor, channels, imgLink }: ContributorDetailsProps) => (
	<div className={classes.contributorDetailsWrapper}>
		<div id="contributor-details-wrapper" className={classes.contributorDetailsTitle}>
			<Avatar
				customClass={classes.contributorAvatar}
				initials={getInitials(contributor.name)}
				size="large"
				imageSrc={imgLink}
			/>
			<div id="contributor-details">
				<Heading
					className={`${classes.contributorDetailsHeading} ${channels?.length ? classes.marginBottom1 : ''}`}
					xs="small"
					md="medium"
					xl="large"
					styleName="largeTitle"
				>
					{contributor.name}
				</Heading>
			</div>
		</div>
	</div>
);

const ContributorPage = () => {
	const classes = useStyles();
	const params = useParams<{ _id: string }>();
	const _id = window.decodeURIComponent(params._id);
	const [isArticleSectionVisible, setIsArticleSectionVisible] = useState(false);

	const { data, loading } = useQuery<{ document: Contributor }>(CONTRIBUTOR_PAGE, {
		skip: !_id,
		fetchPolicy: 'network-only',
		variables: { id: _id }
	});

	useActivityLogger('Contributor', data?.document?._id, data?.document?._id);
	const imgLink = useSecureFileUrl('/contributor-profile-picture/' + window.encodeURIComponent(_id));

	if (!data || loading) {
		return <Loader />;
	}

	const { document: contributor } = data;
	const channels = contributor.channels || [];

	return (
		<Grid className={classes.root} container spacing={8}>
			<Grid item xs={12}>
				<ContributorDetails contributor={contributor} classes={classes} imgLink={imgLink} />
			</Grid>

			{!!channels.length &&
				<Grid className={classes.contributions} item xs={12}>
					<SectionHeader
						id="contributor-contributions-section"
						title={`Channels curated by ${contributor.name}`}
					/>
					<Grid container spacing={2} className={classes.marginTop}>
						<ChannelPreviewList channels={channels} loading={loading} />
					</Grid>
				</Grid>
			}
			<Grid item xs={12} className={isArticleSectionVisible ? classes.visible : classes.hidden}>
				<SectionHeader
					id="contributor-article-section"
					title={`Articles ${contributor.name} has contributed to`}
					subtitle="All the article contributions by this person" />
				<ArticlePreviewList
					contributorId={_id}
					source="Contributor Page"
					onDataLoaded={(numArticles) => setIsArticleSectionVisible(!!numArticles)} />
			</Grid>
		</Grid>
	);
};

export default ContributorPage;
